// Phase 22A — Location Trail hook.
//
// Ping-capture strategy (see PRD):
//   * Runs ONLY while all conditions hold:
//       - user has TA_DA AND READ_GEO_LOCATION workflow perms
//       - GPS permission granted by the browser
//       - user is currently `punched_in` (opts.enabled) and not yet EOD
//   * Adaptive interval: default 60s; drops to 5 min after 3 consecutive
//     stationary pings (< 50m displacement) or when battery <= 20% and not
//     charging; resumes 60s on movement. One GPS fix per tick — there is NO
//     always-on watchPosition (that kept the GPS chip powered continuously
//     and drained a phone from 100% to 3% in ~3 hours).
//   * Batches to `POST /api/location/pings` via the offline SDK
//     (`apiClient.mutate` → same IndexedDB outbox as orders/visits).
//   * Idempotency key on the ping row is `(user_id, ts_ms)` — replays safe.
//   * Never blocks the UI thread; NEVER runs when tab is hidden — the
//     browser suspends timers anyway. We surface this in the PWA-limits doc.
//
// Public state:
//   { enabled, active, gpsPermission, lastPingAt, queuedCount, netStatus }
import { useCallback, useEffect, useRef, useState } from "react";
import { mutate as offlineMutate, isOnline } from "@/offline/apiClient";
import * as outbox from "@/offline/outbox";

const MIN_INTERVAL_MS = 120 * 1000;   // battery: one GPS fix per 2 min while moving (was 1 min)
const IDLE_INTERVAL_MS = 5 * 60 * 1000;
const LOW_BATTERY_INTERVAL_MS = 5 * 60 * 1000;
const LOW_BATTERY_PCT = 20;
const STATIONARY_M = 50;
const STATIONARY_STREAK = 2;
// Battery: every upload wakes the mobile radio (costly on a weak 3G link), so
// batch pings for 2 min instead of flushing every 30s.
const BATCH_FLUSH_MS = 4 * 60 * 1000;

function haversineM(a, b) {
  if (!a || !b) return Infinity;
  const R = 6371000;
  const toRad = (v) => (v * Math.PI) / 180;
  const dp = toRad(b.lat - a.lat);
  const dl = toRad(b.lng - a.lng);
  const p1 = toRad(a.lat), p2 = toRad(b.lat);
  const h = Math.sin(dp / 2) ** 2 + Math.cos(p1) * Math.cos(p2) * Math.sin(dl / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

// Battery Status API — deprecated on iOS, Chrome-only on desktop/Android.
async function readBattery() {
  try {
    if (!navigator.getBattery) return {};
    const b = await navigator.getBattery();
    return { battery_pct: Math.round((b.level || 0) * 100), charging: !!b.charging };
  } catch { return {}; }
}

// Network Information API — Chrome-only. Safari/Firefox return unknown.
function readNetwork() {
  if (!navigator.onLine) return { network_type: "offline" };
  const c = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  if (!c) return { network_type: "unknown" };
  const t = (c.effectiveType || "").toLowerCase();  // "4g" | "3g" | "2g" | "slow-2g"
  if (t === "slow-2g") return { network_type: "2g" };
  if (["4g", "3g", "2g"].includes(t)) return { network_type: t };
  return { network_type: c.type || "unknown" };
}

function parseUA() {
  const ua = navigator.userAgent || "";
  let os = "unknown", os_version, browser = "unknown", browser_version;
  if (/Android/i.test(ua)) { os = "Android"; os_version = (ua.match(/Android ([\d.]+)/) || [])[1]; }
  else if (/iPhone|iPad|iPod/i.test(ua)) { os = "iOS"; os_version = (ua.match(/OS ([\d_]+)/) || [])[1]?.replace(/_/g, "."); }
  else if (/Windows/i.test(ua)) os = "Windows";
  else if (/Mac/i.test(ua)) os = "macOS";
  else if (/Linux/i.test(ua)) os = "Linux";
  if (/Chrome\/([\d.]+)/i.test(ua) && !/Edg|OPR/.test(ua)) {
    browser = "Chrome"; browser_version = ua.match(/Chrome\/([\d.]+)/i)[1];
  } else if (/Safari\/[\d.]+/i.test(ua) && !/Chrome/.test(ua)) {
    browser = "Safari"; browser_version = (ua.match(/Version\/([\d.]+)/) || [])[1];
  } else if (/Firefox\/([\d.]+)/i.test(ua)) {
    browser = "Firefox"; browser_version = ua.match(/Firefox\/([\d.]+)/i)[1];
  }
  return { os, os_version, browser, browser_version, app_build: "22A" };
}

/**
 * @param {{ enabled: boolean, hasTaDa: boolean, hasReadGeo: boolean }} opts
 */
export function useLocationTrail(opts) {
  const { enabled = false, hasTaDa = false, hasReadGeo = false } = opts || {};
  const canTrack = enabled && hasTaDa && hasReadGeo;

  const [gpsPermission, setGpsPermission] = useState("prompt");
  const [active, setActive] = useState(false);
  const [lastPingAt, setLastPingAt] = useState(null);
  const [queuedCount, setQueuedCount] = useState(0);
  const [netStatus, setNetStatus] = useState(isOnline() ? "online" : "offline");

  const bufferRef = useRef([]);         // pings not yet flushed
  const lastPosRef = useRef(null);      // last successful position
  const stationaryStreakRef = useRef(0);
  const pollTimerRef = useRef(null);
  const flushTimerRef = useRef(null);

  // Query GPS permission proactively (best-effort; not supported everywhere).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (navigator.permissions?.query) {
          const s = await navigator.permissions.query({ name: "geolocation" });
          if (!cancelled) setGpsPermission(s.state);
          s.onchange = () => !cancelled && setGpsPermission(s.state);
        }
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // Track net online/offline for the chip
  useEffect(() => {
    const on = () => setNetStatus("online");
    const off = () => setNetStatus("offline");
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, []);

  // Track outbox depth so the chip can show queued count
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const all = await outbox.listAll?.();
        if (cancelled) return;
        setQueuedCount((all || []).filter(x => (x.path || "").includes("/location/pings")).length);
      } catch { /* silent */ }
    };
    tick();
    const t = setInterval(tick, 30000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  const capture = useCallback(() => new Promise((resolve) => {
    if (!navigator.geolocation) return resolve(null);
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude,
                        accuracy_m: p.coords.accuracy }),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 2000 },
    );
  }), []);

  const flush = useCallback(async () => {
    const buf = bufferRef.current;
    if (buf.length === 0) return;
    const [batteryInfo, netInfo, ua] = await Promise.all([readBattery(), readNetwork(), Promise.resolve(parseUA())]);
    const device_health = {
      ...batteryInfo,
      ...netInfo,
      ...ua,
      gps_permission: gpsPermission,
      last_sync_ts: new Date().toISOString(),
    };
    const payload = { pings: [...buf], device_health };
    bufferRef.current = [];
    try {
      await offlineMutate({
        method: "POST", path: "/location/pings", body: payload,
        entity_type: "location_pings", offlineCapable: true,
      });
    } catch {
      // 4xx surfaces here; keep it silent to avoid derailing the SE app.
    }
  }, [gpsPermission]);

  // The polling loop: schedules itself with the current interval.
  const scheduleNext = useCallback((ms) => {
    if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    pollTimerRef.current = setTimeout(async () => {
      const pos = await capture();
      // A fix worse than 1 km (a desktop browser guessing from the internet address) is not a real
      // position: it is never recorded (the loop below still schedules the next try).
      if (pos && (pos.accuracy_m || 0) <= 1000) {
        const now = new Date();
        const displacement = haversineM(lastPosRef.current, pos);
        if (lastPosRef.current && displacement < STATIONARY_M) {
          stationaryStreakRef.current += 1;
        } else {
          stationaryStreakRef.current = 0;
        }
        lastPosRef.current = pos;
        bufferRef.current.push({
          ts_client: now.toISOString(),
          lat: pos.lat, lng: pos.lng, accuracy_m: pos.accuracy_m,
          source: "interval",
        });
        setLastPingAt(now);
      }
      const batt = await readBattery();
      const lowBattery = batt.battery_pct !== undefined
        && batt.battery_pct <= LOW_BATTERY_PCT && !batt.charging;
      const nextInterval = lowBattery ? LOW_BATTERY_INTERVAL_MS
        : stationaryStreakRef.current >= STATIONARY_STREAK
          ? IDLE_INTERVAL_MS : MIN_INTERVAL_MS;
      scheduleNext(nextInterval);
    }, ms);
  }, [capture]);

  useEffect(() => {
    if (!canTrack) {
      setActive(false);
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
      if (flushTimerRef.current) clearInterval(flushTimerRef.current);
      // Best-effort final flush of any buffered pings.
      flush();
      return;
    }
    if (!navigator.geolocation) { setActive(false); return; }
    setActive(true);
    stationaryStreakRef.current = 0;
    lastPosRef.current = null;
    scheduleNext(MIN_INTERVAL_MS);
    flushTimerRef.current = setInterval(flush, BATCH_FLUSH_MS);
    return () => {
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
      if (flushTimerRef.current) clearInterval(flushTimerRef.current);
      flush();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canTrack]);

  return { active, canTrack, gpsPermission, lastPingAt, queuedCount, netStatus,
           bufferSize: bufferRef.current.length,
           _test: { haversineM } };
}
